import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Layers, Loader2, MapPin, RefreshCw } from 'lucide-react'
import type { Map as MapboxMap, MapMouseEvent, Popup as MapboxPopup } from 'mapbox-gl'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { Field } from '@/shared/ui/Modal'
import { EmptyState, ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { hasMapbox, loadMapbox, styleUrl, KENYA_BOUNDS, type MapStyleChoice } from '@/shared/map/mapbox'
import { extractRings, ringBounds } from '@/shared/map/geo'
import { VectorPreview } from '@/shared/map/VectorPreview'
import { cn } from '@/lib/utils'
import { listUnits, unitFeatures, EMPTY_UNIT_FILTERS } from './api'
import { useLoad } from '@/features/administration/hooks'
import { RISK_LABEL, RISK_ORDER, UNIT_TYPE_LABEL, type GeoJsonObject } from '@/shared/api/types'

/** Short forms for the "Within" options — "Polling station: X" is never offered. */
const UNIT_TYPE_SHORT: Record<string, string> = {
  COUNTY: 'County', CONSTITUENCY: 'Constituency', WARD: 'Ward',
}

/** Which value paints the map. Not "colour by anything" — three that mean something. */
type Metric = 'risk' | 'turnout' | 'voters'

const METRICS: { id: Metric; label: string; hint: string }[] = [
  { id: 'risk', label: 'Risk', hint: 'Assessed risk level. Grey means not assessed — not low.' },
  { id: 'turnout', label: 'Turnout', hint: 'Most recent recorded turnout. Grey means none recorded.' },
  { id: 'voters', label: 'Voters', hint: 'Registered voters, relative to the largest unit shown.' },
]

const LEVELS = ['COUNTY', 'CONSTITUENCY', 'WARD', 'POLLING_STATION'] as const

/**
 * The whole level at once.
 *
 * `GET /geography/features` returns every child geometry of a level in ONE
 * request — polygons for the area types, points for stations — simplified in
 * PostGIS. Drawing a county's 1,450 wards by fetching each boundary separately is
 * 1,450 requests, which is why this endpoint exists and why the map view waited
 * for it.
 *
 * The colour scales deliberately leave "not assessed" grey rather than folding it
 * into the bottom of the ramp. A choropleth that paints unassessed wards the same
 * green as safe ones is worse than no choropleth: it manufactures confidence.
 */
export function MapView({ onOpenUnit }: { onOpenUnit: (id: string) => void }) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapboxMap | null>(null)
  const popup = useRef<MapboxPopup | null>(null)
  const open = useRef(onOpenUnit)
  open.current = onOpenUnit

  const [level, setLevel] = useState<string>('COUNTY')
  /** Optional ancestor narrowing — a DESCENDANT predicate, so a county id with
   *  `level = WARD` draws that county's wards. */
  const [within, setWithin] = useState<string>('')
  const [nonce, setNonce] = useState(0)
  const [metric, setMetric] = useState<Metric>('risk')
  const [style, setStyle] = useState<MapStyleChoice>('theme')
  const [data, setData] = useState<GeoJsonObject | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))

  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() => setDark(el.classList.contains('dark')))
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])

  // ── data ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    // `nonce` only changes when Refresh is pressed. The response is cached
    // `private, max-age=300`, so without it the browser serves a five-minute-old
    // map from disk after a boundary upload and never asks the server.
    unitFeatures({ type: level, parentId: within || undefined, nonce: nonce || undefined })
      .then((g) => { if (!cancelled) setData(g) })
      .catch((err) => {
        if (cancelled) return
        setError(explain(err))
        setData(null)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [level, within, nonce])

  const features = useMemo(() => {
    const list = Array.isArray(data?.features) ? data.features : []
    return list as unknown as MapFeature[]
  }, [data])

  const omitted = useMemo(() => {
    const props = (data as { properties?: { omittedNoGeometry?: number } } | null)?.properties
    return props?.omittedNoGeometry ?? 0
  }, [data])

  /** Largest register in the current set — the denominator for the voters ramp. */
  const maxVoters = useMemo(
    () => features.reduce((m, f) => Math.max(m, f.properties?.registeredVoters ?? 0), 0),
    [features],
  )

  const isPoint = level === 'POLLING_STATION'

  /**
   * Ancestors you can narrow to. Counties and constituencies only: narrowing to a
   * ward leaves at most a few dozen stations, which the level selector already
   * covers, and listing 1,450 wards in a dropdown is not a control.
   */
  const ancestors = useLoad(
    () => listUnits({ ...EMPTY_UNIT_FILTERS, type: ['COUNTY', 'CONSTITUENCY'], sort: 'name,asc' }, 0, 200),
    [],
  )
  const ancestorOptions = ancestors.data?.content ?? []

  // ── the map itself ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasMapbox() || !host.current) return
    let cancelled = false
    loadMapbox().then((mapboxgl) => {
      if (cancelled || !host.current) return
      const m = new mapboxgl.Map({
        container: host.current,
        style: styleUrl(style, dark),
        bounds: KENYA_BOUNDS,
        fitBoundsOptions: { padding: 24 },
        cooperativeGestures: true,
      })
      m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
      m.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left')
      m.on('load', () => { if (!cancelled) setReady(true) })
      m.on('error', () => { if (!cancelled) setError('The basemap could not load.') })
      map.current = m
    }).catch(() => { if (!cancelled) setError('The basemap could not load.') })
    return () => {
      cancelled = true
      popup.current?.remove()
      map.current?.remove()
      map.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!map.current || !ready) return
    map.current.setStyle(styleUrl(style, dark))
  }, [style, dark, ready])

  // ── layers ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || !ready || !data) return

    const paint = () => {
      const coloured = colourise(features, metric, maxVoters)
      const src = m.getSource('axis-features')
      if (src) {
        ;(src as { setData: (d: unknown) => void }).setData(coloured)
      } else {
        m.addSource('axis-features', { type: 'geojson', data: coloured as never })
        m.addLayer({
          id: 'axis-fill',
          type: 'fill',
          source: 'axis-features',
          filter: ['!=', ['geometry-type'], 'Point'],
          paint: { 'fill-color': ['get', 'axisColour'], 'fill-opacity': 0.55 },
        })
        m.addLayer({
          id: 'axis-line',
          type: 'line',
          source: 'axis-features',
          filter: ['!=', ['geometry-type'], 'Point'],
          paint: { 'line-color': '#00000033', 'line-width': 0.6 },
        })
        m.addLayer({
          id: 'axis-point',
          type: 'circle',
          source: 'axis-features',
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2.5, 12, 6],
            'circle-color': ['get', 'axisColour'],
            'circle-stroke-width': 0.5,
            'circle-stroke-color': '#00000055',
          },
        })
      }

      const b = ringBounds(extractRings(coloured))
      if (b) {
        m.fitBounds([[b.minLon, b.minLat], [b.maxLon, b.maxLat]], { padding: 24, duration: 0 })
      }
    }

    if (m.isStyleLoaded()) paint()
    m.on('style.load', paint)
    return () => { m.off('style.load', paint) }
  }, [data, features, metric, maxVoters, ready])

  // ── hover and click ───────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || !ready) return
    let cancelled = false

    void loadMapbox().then((mapboxgl) => {
      if (cancelled || !map.current) return
      const layers = ['axis-fill', 'axis-point']

      const onMove = (e: LayerMouseEvent) => {
        const f = e.features?.[0]
        if (!f) return
        m.getCanvas().style.cursor = 'pointer'
        popup.current ??= new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(tooltipHtml(f.properties as MapFeatureProps))
          .addTo(m)
      }
      const onLeave = () => {
        m.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
      const onClick = (e: LayerMouseEvent) => {
        const id = (e.features?.[0]?.properties as MapFeatureProps | undefined)?.id
        if (id) open.current(id)
      }

      for (const l of layers) {
        m.on('mousemove', l, onMove)
        m.on('mouseleave', l, onLeave)
        m.on('click', l, onClick)
      }
    })

    return () => { cancelled = true }
  }, [ready])

  const controls = (
    <div className="flex items-end gap-3 flex-wrap">
      <Field label="Level">
        <select value={level} onChange={(e) => setLevel(e.target.value)} className="erp-input">
          {LEVELS.map((l) => (
            <option key={l} value={l}>{UNIT_TYPE_LABEL[l] ?? l}</option>
          ))}
        </select>
      </Field>
      <Field label="Within">
        <select value={within} onChange={(e) => setWithin(e.target.value)} className="erp-input max-w-56">
          <option value="">Everywhere in scope</option>
          {ancestorOptions
            .filter((a) => a.unitType !== level)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {UNIT_TYPE_SHORT[a.unitType] ?? a.unitType}: {a.name}
              </option>
            ))}
        </select>
      </Field>
      <Field label="Colour by">
        <select value={metric} onChange={(e) => setMetric(e.target.value as Metric)}
                className="erp-input">
          {METRICS.map((mt) => <option key={mt.id} value={mt.id}>{mt.label}</option>)}
        </select>
      </Field>
      {hasMapbox() && (
        <button
          className="erp-btn"
          onClick={() => setStyle((v) => (v === 'satellite' ? 'theme' : 'satellite'))}
        >
          <Layers className="h-3.5 w-3.5" />
          {style === 'satellite' ? 'Map' : 'Satellite'}
        </button>
      )}
      <button className="erp-btn" onClick={() => setNonce((n) => n + 1)} disabled={loading}
              title="Bypass the five-minute response cache">
        <RefreshCw className="h-3.5 w-3.5" /> Refresh
      </button>
      {loading && (
        <span className="text-erp-xs text-muted-foreground inline-flex items-center gap-1.5 pb-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading {fmtNumber(features.length)} features…
        </span>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Map"
        description="One level at a time, in a single request. Click a unit to open it."
      />

      <ErrorStrip message={error} />
      {controls}

      <p className="text-erp-xs text-muted-foreground">
        {METRICS.find((mt) => mt.id === metric)?.hint}
      </p>

      {!hasMapbox() ? (
        <NoBasemapFallback features={features} />
      ) : (
        <Panel bodyClassName="p-0">
          <div className="relative h-[32rem]">
            <div ref={host} className="absolute inset-0" role="img" aria-label="Map of electoral units" />
            {!ready && (
              <div className="absolute inset-0 grid place-items-center bg-muted/40 text-erp-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading the basemap…
                </span>
              </div>
            )}
          </div>
        </Panel>
      )}

      <Legend metric={metric} max={maxVoters} />

      <div className="flex items-center gap-4 flex-wrap text-erp-xs text-muted-foreground">
        <span>
          {fmtNumber(features.length)} {isPoint ? 'station(s)' : 'unit(s)'} drawn
        </span>
        {/* Counted server-side by a separate query, so it is a real number. Note it
            ignores `bbox` — with a viewport filter it reports geometry-less units
            across the whole selection, not just the visible part. */}
        {omitted > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 text-amber-600" />
            {fmtNumber(omitted)} omitted — no {isPoint ? 'coordinates' : 'boundary'} yet.
            They exist in the table; they just cannot be drawn.
          </span>
        )}
      </div>

      {features.length === 0 && !loading && !error && (
        <EmptyState
          icon={<MapPin className="h-4 w-4" />}
          title="Nothing to draw at this level"
          hint={isPoint
            ? 'No station has coordinates yet. Import them, or set them on a station.'
            : 'No unit at this level has a boundary yet. Load them through the boundary import.'}
        />
      )}
    </div>
  )
}

// ── colour ──────────────────────────────────────────────────────────────────

interface MapFeatureProps {
  id: string
  code: string
  name: string
  unitType: string
  registeredVoters?: number | null
  stationCount?: number | null
  riskLevel?: string | null
  turnoutPct?: number | null
  moodIndex?: number | null
  axisColour?: string
}
interface MapFeature { properties?: MapFeatureProps; geometry?: unknown; type?: string }
/** `MapMouseEvent` only carries `features` when the listener is bound to a layer. */
type LayerMouseEvent = MapMouseEvent & { features?: { properties?: unknown }[] }

/** Grey. Every scale reserves it for "not assessed", and nothing else uses it. */
const UNASSESSED = '#c7c2bd'

const RISK_COLOUR: Record<string, string> = {
  LOW: '#10b981',
  MEDIUM: '#f59e0b',
  HIGH: '#f97316',
  CRITICAL: '#dc2626',
}

/** Chocolate ramp, light → dark. Sequential, because turnout and register are levels. */
const RAMP = ['#f3e4d6', '#e0c3a3', '#c99b70', '#a97544', '#7B3F00']

function rampColour(t: number): string {
  const i = Math.min(RAMP.length - 1, Math.max(0, Math.floor(t * RAMP.length)))
  return RAMP[i] ?? UNASSESSED
}

/**
 * Writes an `axisColour` property onto every feature.
 *
 * Mapbox can express this as a style expression, but doing it here keeps the
 * "unassessed is grey, not the bottom of the ramp" rule in one readable place
 * instead of a nested `case`/`interpolate` expression nobody will re-read.
 */
function colourise(features: MapFeature[], metric: Metric, maxVoters: number): GeoJsonObject {
  return {
    type: 'FeatureCollection',
    features: features.map((f) => {
      const p = f.properties ?? ({} as MapFeatureProps)
      let colour = UNASSESSED
      if (metric === 'risk' && p.riskLevel) {
        colour = RISK_COLOUR[p.riskLevel] ?? UNASSESSED
      } else if (metric === 'turnout' && p.turnoutPct != null) {
        colour = rampColour(Math.min(1, p.turnoutPct / 100))
      } else if (metric === 'voters' && maxVoters > 0 && (p.registeredVoters ?? 0) > 0) {
        colour = rampColour((p.registeredVoters ?? 0) / maxVoters)
      }
      return { ...f, properties: { ...p, axisColour: colour } }
    }),
  } as unknown as GeoJsonObject
}

function escapeHtml(v: string): string {
  return v.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c
  ))
}

function tooltipHtml(p: MapFeatureProps): string {
  // Unit names come from an IEBC file, i.e. from outside — escaped, not trusted.
  const rows = [
    ['Voters', p.registeredVoters == null ? '—' : fmtNumber(p.registeredVoters)],
    ['Turnout', p.turnoutPct == null ? '—' : `${p.turnoutPct}%`],
    ['Risk', p.riskLevel ? RISK_LABEL[p.riskLevel] ?? p.riskLevel : '—'],
  ]
  return `<div style="font-size:12px;line-height:1.4">
    <strong>${escapeHtml(p.name ?? '')}</strong>
    <span style="opacity:.6"> ${escapeHtml(p.code ?? '')}</span>
    ${rows.map(([k, v]) => `<div style="opacity:.75">${k}: ${escapeHtml(String(v))}</div>`).join('')}
  </div>`
}

function Legend({ metric, max }: { metric: Metric; max: number }) {
  if (metric === 'risk') {
    return (
      <div className="flex items-center gap-3 flex-wrap text-erp-xs">
        {RISK_ORDER.slice().reverse().map((r) => (
          <span key={r} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: RISK_COLOUR[r] }} />
            {RISK_LABEL[r]}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: UNASSESSED }} />
          Not assessed
        </span>
      </div>
    )
  }
  const top = metric === 'turnout' ? '100%' : fmtNumber(max)
  return (
    <div className="flex items-center gap-3 flex-wrap text-erp-xs">
      <span className="text-muted-foreground">0</span>
      <span className="inline-flex rounded-sm overflow-hidden">
        {RAMP.map((c) => <span key={c} className="h-2.5 w-8" style={{ background: c }} />)}
      </span>
      <span className="text-muted-foreground">{top}</span>
      <span className="inline-flex items-center gap-1.5 text-muted-foreground ml-2">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: UNASSESSED }} />
        None recorded
      </span>
    </div>
  )
}

/**
 * No token: draw the outlines anyway.
 *
 * Not a map — no basemap, no zoom, no click-through — but it answers "do these
 * boundaries look like the right country" without an account, which is what the
 * fallback is for. Point levels have nothing useful to show this way and say so.
 */
function NoBasemapFallback({ features }: { features: MapFeature[] }) {
  const rings = useMemo(
    () => features.flatMap((f) => extractRings({ type: 'Feature', geometry: f.geometry } as GeoJsonObject)),
    [features],
  )
  if (rings.length === 0) {
    return (
      <Note tone="info">
        No map key is configured, so there is no basemap. Set{' '}
        <code className="font-mono">AXIS_MAPBOX_TOKEN</code> to enable the map view — the
        table and the per-unit outlines work without it.
      </Note>
    )
  }
  return (
    <div className="space-y-2">
      <VectorPreview rings={rings} label="all units" className={cn('h-[28rem]')} />
      <p className="text-erp-xs text-muted-foreground">
        Outlines only — no map key is configured, so there is no basemap and no
        click-through. Set <code className="font-mono">AXIS_MAPBOX_TOKEN</code> for the
        full map.
      </p>
    </div>
  )
}
